"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import { FullPageSkeleton } from "@/components/skeletons/FullPageSkeleton";
import {
  Search,
  BookOpen,
  FileText,
  Users,
  ChevronRight,
  Loader2,
  GraduationCap,
  School,
  Filter,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { apiFetch, versioned } from "@/lib/api";
import { SCHOOLS } from "@/data/schools";
import courseCatalog from "@/data/courses.json";
import { useMetadata } from "@/hooks/useMetadata";
import { useMyCourses } from "@/hooks/useMyCourses";
import { useAuth } from "@/lib/AuthContext";

/**
 * Curriculum metadata keyed by a normalised course code (uppercase, no spaces)
 * so both "AAE 201" and "AAE201" resolve. `GET /api/v1/courses` returns bare
 * code_strings, so this is what supplies the title/level/department the cards
 * render.
 */
const COURSE_META = new Map<
  string,
  { title: string; level: string; department: string }
>(
  (
    courseCatalog as Array<{
      code?: string;
      title?: string;
      level?: string;
      department?: string;
    }>
  )
    .filter((c) => typeof c?.code === "string")
    .map((c) => [
      String(c.code).toUpperCase().replace(/\s+/g, ""),
      {
        title: String(c.title || ""),
        level: String(c.level || ""),
        department: String(c.department || ""),
      },
    ]),
);

/* --- Types --- */
type Course = {
  id: string;
  code: string;
  name: string;
  department: string;
  school: string;
  level: number;
  chunks: number;
  pastQuestions: number;
  studentsEnrolled: number;
};

type Filters = {
  search: string;
  school: string;
  department: string;
  level: string;
};

type RawCourse = {
  id?: unknown;
  course_id?: unknown;
  code?: unknown;
  course_code?: unknown;
  title?: unknown;
  name?: unknown;
  course_name?: unknown;
  department?: unknown;
  department_name?: unknown;
  school?: unknown;
  school_name?: unknown;
  level?: unknown;
  year?: unknown;
  chunks?: unknown;
  chunk_count?: unknown;
  material_count?: unknown;
  pastQuestions?: unknown;
  past_questions?: unknown;
  question_count?: unknown;
  studentsEnrolled?: unknown;
  students_enrolled?: unknown;
  enrollment_count?: unknown;
};

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

const ITEMS_PER_PAGE = 20;

const LEVELS = ["200", "300"];

/* --- Copy --- */
const COURSES_COPY = {
  header: {
    title: "Course Catalogue",
    subtext:
      "Explore all courses available on UniUI, with materials, past questions, and AI-powered study tools.",
  },
  filters: {
    searchPlaceholder: "Search by course code or title…",
    schoolLabel: "School",
    departmentLabel: "Department",
    levelLabel: "Level",
    all: "All",
    clear: "Clear Filters",
  },
  card: {
    viewCourse: "View Course",
    chunksLabel: "Chunks",
    pastQuestionsLabel: "Past Questions",
    studentsLabel: "Students",
  },
  empty: {
    title: "No courses found",
    subtext: "Try adjusting your search or filters.",
  },
  loadMore: "Load More",
  loading: "Loading courses…",
};

/* --- Debounce hook --- */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export default function CoursesPage() {
  useMetadata({ key: "courses" });
  const { user } = useAuth();
  const { courses: myCourses, loading: coursesLoading } = useMyCourses();
  // Enrolled course codes (normalised) for the "Enrolled" badge — does NOT
  // restrict the catalogue (per #16 the user wants to browse ALL courses).
  const myCourseSet = useMemo(
    () => new Set(myCourses.map((c) => c.toUpperCase().replace(/\s+/g, ""))),
    [myCourses],
  );
  const [courses, setCourses] = useState<Course[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [filters, setFilters] = useState<Filters>({
    search: "",
    school: "__all__",
    department: "__all__",
    level: "__all__",
  });

  const debouncedSearch = useDebounce(filters.search, 300);

  const updateFilter = useCallback(
    (key: keyof Filters) => (value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setOffset(0);
      setHasMore(true);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      school: "__all__",
      department: "__all__",
      level: "__all__",
    });
    setOffset(0);
    setHasMore(true);
  }, []);

  /* --- Fetch courses --- */
  useEffect(() => {
    let cancelled = false;

    async function loadCourses(isLoadMore = false) {
      if (!isLoadMore) setLoading(true);
      else setLoadingMore(true);

      // Wait for myCourses to finish loading (if user is logged in) so we can
      // filter the catalog by the user's onboarding-selected courses.
      if (user && coursesLoading && !isLoadMore) return;

      try {
        const params = new URLSearchParams();
        // Backend GET /api/v1/courses supports ?search= and ?user_id= only.
        // school/department/level filters are handled client-side.
        if (debouncedSearch.trim())
          params.set("search", debouncedSearch.trim());

        const data = await apiFetch<unknown>(
          versioned(`/api/v1/courses?${params.toString()}`),
        );

        if (cancelled) return;

        let rawList: RawCourse[];
        if (Array.isArray(data)) {
          // Backend GET /api/v1/courses returns a bare string[] of course CODES.
          // Enrich each code from the local curriculum catalog so the card can
          // render the real title / level / department. Without_this, `c.code`
          // is undefined on a string and every card rendered as
          // "UNKNOWN / Untitled Course" — the reported bug.
          rawList = data.map((item, idx): RawCourse => {
            if (typeof item === "string") {
              const meta = COURSE_META.get(
                item.toUpperCase().replace(/\s+/g, ""),
              );
              return {
                course_id: item,
                course_code: item,
                title: meta?.title,
                level: meta?.level,
                department: meta?.department,
              } as RawCourse;
            }
            return item as RawCourse;
          });
        } else {
          const payload = data as {
            courses?: RawCourse[];
            items?: RawCourse[];
            data?: RawCourse[];
          };
          rawList = payload.courses || payload.items || payload.data || [];
        }

        const normalised: Course[] = rawList.map((c, idx) => ({
          id: asString(c.id || c.course_id) || String(idx),
          code: asString(c.code || c.course_code || c.title) || "UNKNOWN",
          name: asString(c.name || c.title || c.course_name) || "",
          department: asString(c.department || c.department_name) || "",
          school: asString(c.school || c.school_name) || "",
          level:
            typeof c.level === "number"
              ? c.level
              : parseInt(asString(c.level || c.year) || "1", 10),
          chunks: asNumber(c.chunks ?? c.chunk_count ?? c.material_count),
          pastQuestions: asNumber(
            c.pastQuestions ?? c.past_questions ?? c.question_count,
          ),
          studentsEnrolled: asNumber(
            c.studentsEnrolled ?? c.students_enrolled ?? c.enrollment_count,
          ),
        }));

        // Client-side filter by school/department/level (backend doesn't support these)
        let filtered = normalised;
        if (filters.school && filters.school !== "__all__") {
          filtered = filtered.filter((c) => c.school === filters.school);
        }
        if (filters.department && filters.department !== "__all__") {
          filtered = filtered.filter(
            (c) => c.department === filters.department,
          );
        }
        if (filters.level && filters.level !== "__all__") {
          filtered = filtered.filter((c) => String(c.level) === filters.level);
        }

        // Per #16: show the FULL course catalogue so users can scroll and enrol
        // the courses they want — not a locked/enrolled-only list. Enrolled
        // courses are highlighted with a badge in the card below.
        void myCourseSet;

        setCourses((prev) => (isLoadMore ? [...prev, ...filtered] : filtered));
        // The catalogue endpoint returns the entire list in one response (it is
        // not paginated), so there is nothing further to page through.
        setHasMore(false);
        setOffset((prev) =>
          isLoadMore ? prev + ITEMS_PER_PAGE : ITEMS_PER_PAGE,
        );

        if (filtered.length > 0 && departments.length === 0) {
          const uniqueDepts = Array.from(
            new Set(normalised.map((c) => c.department).filter(Boolean)),
          ) as string[];
          setDepartments(uniqueDepts);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load courses.");
          if (!isLoadMore) setCourses([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [
    debouncedSearch,
    filters.school,
    filters.department,
    filters.level,
    myCourses,
    coursesLoading,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextOffset = offset;
    setOffset(nextOffset);
    setLoadingMore(true);

    try {
      const params = new URLSearchParams();
      params.set("limit", ITEMS_PER_PAGE.toString());
      params.set("offset", nextOffset.toString());
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (filters.school && filters.school !== "__all__")
        params.set("school", filters.school);
      if (filters.department && filters.department !== "__all__")
        params.set("department", filters.department);
      if (filters.level && filters.level !== "__all__")
        params.set("level", filters.level);

      const data = await apiFetch<unknown>(
        versioned(`/api/v1/courses?${params.toString()}`),
      );
      const payload = data as {
        courses?: RawCourse[];
        items?: RawCourse[];
        data?: RawCourse[];
      };
      const rawList: RawCourse[] = Array.isArray(data)
        ? (data as RawCourse[])
        : payload.courses || payload.items || payload.data || [];
      const normalised: Course[] = rawList.map((c, idx) => ({
        id: asString(c.id || c.course_id) || String(idx),
        code: asString(c.code || c.course_code || c.title) || "UNKNOWN",
        name: asString(c.name || c.title || c.course_name),
        department: asString(c.department || c.department_name),
        school: asString(c.school || c.school_name),
        level:
          typeof c.level === "number"
            ? c.level
            : parseInt(asString(c.level || c.year) || "1", 10),
        chunks: asNumber(c.chunks ?? c.chunk_count ?? c.material_count),
        pastQuestions: asNumber(
          c.pastQuestions ?? c.past_questions ?? c.question_count,
        ),
        studentsEnrolled: asNumber(
          c.studentsEnrolled ?? c.students_enrolled ?? c.enrollment_count,
        ),
      }));

      setCourses((prev) => [...prev, ...normalised]);
      setHasMore(normalised.length >= ITEMS_PER_PAGE);
      setOffset((prev) => prev + ITEMS_PER_PAGE);
    } catch {
      toast.error("Failed to load more courses.");
    } finally {
      setLoadingMore(false);
    }
  }, [offset, loadingMore, hasMore, debouncedSearch, filters]);

  const getInitials = (str: string) =>
    str
      .split(/[\s ]+/)
      .map((s) => s.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");

  // The full-page skeleton used to short-circuit the whole tree while the
  // client fetched courses, so the <h1> only ever existed after hydration.
  // Crawlers and crawlers-only renderers can therefore see a page with no
  // heading at all. Render the real header (title + subtext) immediately and
  // swap only the results grid to a skeleton while loading.
  const showSkeleton = loading;

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 py-12 space-y-10">
          {/* -- Header -- */}
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-3"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <GraduationCap className="w-8 h-8 text-y" />
              <h1 className="display text-4xl md:text-5xl tracking-tight">
                {COURSES_COPY.header.title}
              </h1>
            </div>
            <p className="text-muted-foreground font-medium max-w-2xl mx-auto leading-relaxed">
              {COURSES_COPY.header.subtext}
            </p>
          </motion.header>
          {showSkeleton ? (
            <FullPageSkeleton variant="dashboard" />
          ) : (
            <>
          {/* -- Filters -- */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="matte-metal p-4 md:p-6 rounded-[2rem]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div className="lg:col-span-2 space-y-2">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                  Search
                </span>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={COURSES_COPY.filters.searchPlaceholder}
                    value={filters.search}
                    onChange={(e) => updateFilter("search")(e.target.value)}
                    className="pl-9 h-10 rounded-xl text-base md:text-xs"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                  {COURSES_COPY.filters.schoolLabel}
                </span>
                <Select
                  value={filters.school}
                  onValueChange={updateFilter("school")}
                >
                  <SelectTrigger className="rounded-xl h-10 text-base md:text-xs">
                    <SelectValue placeholder={COURSES_COPY.filters.all} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {COURSES_COPY.filters.all}
                    </SelectItem>
                    {SCHOOLS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                  {COURSES_COPY.filters.levelLabel}
                </span>
                <Select
                  value={filters.level}
                  onValueChange={updateFilter("level")}
                >
                  <SelectTrigger className="rounded-xl h-10 text-base md:text-xs">
                    <SelectValue placeholder={COURSES_COPY.filters.all} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {COURSES_COPY.filters.all}
                    </SelectItem>
                    {LEVELS.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}-level
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
                  &nbsp;
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full rounded-xl text-xs font-black uppercase tracking-widest"
                >
                  <X className="w-3 h-3 mr-1" />
                  {COURSES_COPY.filters.clear}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* -- Course Grid -- */}
          {courses.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20 space-y-4"
            >
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground" />
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
                {COURSES_COPY.empty.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {COURSES_COPY.empty.subtext}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {courses.map((course, i) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                >
                  <Card className="h-full rounded-2xl border border-border bg-card hover:border-y/40 transition-all duration-300 group">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-2">
                          <CardTitle className="text-y font-black font-mono text-lg tracking-tight leading-none">
                            {course.code}
                          </CardTitle>
                          <p className="text-xs font-bold text-foreground leading-relaxed line-clamp-2">
                            {course.name || "Untitled Course"}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-y/10 flex items-center justify-center shrink-0">
                          <GraduationCap className="w-5 h-5 text-y" />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {course.department && (
                          <Badge
                            variant="secondary"
                            className="text-xs font-black uppercase tracking-wider"
                          >
                            {course.department}
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className="text-xs font-black uppercase tracking-wider"
                        >
                          {course.level}-level
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/*
                        `GET /api/v1/courses` returns bare course codes and carries no
                        per-course counts, so these were always rendering a hard "0"
                        ("0 chunks / 0 past questions"). Showing an em-dash for an
                        unknown count is honest; a real number is shown whenever the
                        payload actually provides one. Per-course truth is available
                        from `GET /api/v1/courses/{code}/stats` (Neon-backed) and_is
________________________used_on_the_course_detail_surfaces_rather_than_2,426 cards.
                      */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-xl bg-background border border-border text-center space-y-1 min-w-0">
                          <BookOpen className="w-3.5 h-3.5 text-y mx-auto" />
                          <p className="text-xs font-black">
                            {course.chunks > 0 ? course.chunks : "—"}
                          </p>
                          <span className="text-xs text-muted-foreground uppercase tracking-widest break-words">
                            {COURSES_COPY.card.chunksLabel}
                          </span>
                        </div>
                        <div className="p-3 rounded-xl bg-background border border-border text-center space-y-1 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-y mx-auto" />
                          <p className="text-xs font-black">
                            {course.pastQuestions > 0
                              ? course.pastQuestions
                              : "—"}
                          </p>
                          <span className="text-xs text-muted-foreground uppercase tracking-widest break-words">
                            {COURSES_COPY.card.pastQuestionsLabel}
                          </span>
                        </div>
                        <div className="p-3 rounded-xl bg-background border border-border text-center space-y-1 min-w-0">
                          <Users className="w-3.5 h-3.5 text-y mx-auto" />
                          <p className="text-xs font-black">
                            {course.studentsEnrolled > 0
                              ? course.studentsEnrolled
                              : "—"}
                          </p>
                          <span className="text-xs text-muted-foreground uppercase tracking-widest break-words">
                            {COURSES_COPY.card.studentsLabel}
                          </span>
                        </div>
                      </div>
                      <Link
                        href={`/course/${course.id}/syllabus`}
                        className="block"
                      >
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full rounded-xl font-black uppercase tracking-widest text-xs group-hover:shadow-[0_0_20px_rgba(255,214,0,0.25)] transition-all"
                        >
                          {COURSES_COPY.card.viewCourse}
                          <ChevronRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* -- Load More -- */}
          {hasMore && courses.length > 0 && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                size="lg"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="rounded-xl font-black uppercase tracking-widest text-xs"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {COURSES_COPY.loading}
                  </>
                ) : (
                  COURSES_COPY.loadMore
                )}
              </Button>
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </PageTransition>
  );
}